import * as React from 'react'
import styled from 'styled-components'
import I18n from 'core/i18n'
import * as constants from 'static/constants'
import { Button, InputText } from 'components/atoms'

interface IExpenseInput {
  id?: number
  name: string
  expense: number | null
  image: string | undefined
  image_url: string
  content_type: string | null
  filename: string | null
}
interface IProps {
  expenses: IExpenseInput[]
  setExpenses: React.Dispatch<React.SetStateAction<IExpenseInput[]>>
}

const MultipleExpenseInput = ({ expenses, setExpenses }: IProps) => {
  const handleDelete = React.useCallback(
    (index: number) => {
      setExpenses(oldValues => oldValues.filter((_, i) => i !== index))
    },
    [setExpenses]
  )

  const handleAdd = React.useCallback(() => {
    setExpenses(oldValues => [
      ...oldValues,
      {
        name: '',
        expense: null,
        image: '',
        image_url: '',
        id: null,
        content_type: null,
        filename: null,
      },
    ])
  }, [setExpenses])

  const handleUpdateExpenses = React.useCallback(
    (index: number, params: Partial<IExpenseInput>) => {
      setExpenses(oldValues =>
        index === -1
          ? oldValues
          : [
              ...oldValues.slice(0, index),
              { ...oldValues[index], ...params },
              ...oldValues.slice(index + 1),
            ]
      )
    },
    [setExpenses]
  )

  const handleFileChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
      const file = e.target.files?.[0]
      if (file) {
        const reader = new FileReader()
        reader.onload = event => {
          handleUpdateExpenses(index, {
            image: (file as unknown) as string,
            image_url: event.target?.result as string,
            content_type: file.type,
            filename: file.name,
          })
        }
        reader.readAsDataURL(file)
      }
    },
    [handleUpdateExpenses]
  )

  return (
    <Wrapper>
      <hr />
      {expenses.map((expense, index) => (
        <div className="expenseItem" key={index}>
          <div className="expenseTitle">Expense {index + 1}</div>
          <InputText
            required={true}
            name="name"
            placeholder="ex) バス代"
            value={expense.name}
            onChangeHandler={e => handleUpdateExpenses(index, { name: e.target.value })}
          />
          <InputText
            required={true}
            inputMode="numeric"
            name="expense"
            placeholder="ex) 500"
            value={expense.expense}
            onChangeHandler={e => handleUpdateExpenses(index, { expense: e.target.value })}
          />
          <input
            type="file"
            id={`expenseImage${index}`}
            onChange={e => handleFileChange(e, index)}
            accept="image/*, application/pdf"
          />
          <label htmlFor={`expenseImage${index}`} className="expenseImageUpload">
            {expense.image_url ? (
              <div className="expenseImage">
                {expense.content_type === 'application/pdf' ? (
                  <p>{expense.filename}</p>
                ) : (
                  <img
                    src={expense.image_url}
                    onClick={e => {
                      e.preventDefault()
                      e.stopPropagation()
                      window.open(expense.image_url, '_blank', 'noopener,noreferrer')
                    }}
                    title="クリックで費用を別タブに表示"
                  />
                )}
              </div>
            ) : (
              <Button small={true} fake={true}>
                Upload
              </Button>
            )}
          </label>
          <Button
            small={true}
            textColor={constants.COLORS.white}
            backgroundColor={constants.COLORS.caution}
            handleClick={() => {
              handleDelete(index)
            }}
          >
            {I18n.t('generic.delete')}
          </Button>
        </div>
      ))}
      <div className="addExpenseBtn">
        <Button small={true} handleClick={handleAdd} disabled={expenses.length === 5}>
          + {I18n.t('generic.add')} {I18n.t('guide_report_expense.expense')}
        </Button>
      </div>
    </Wrapper>
  )
}

export default MultipleExpenseInput

const Wrapper = styled.div`
  .expenseItem {
    display: flex;
    gap: 8px;
    align-items: center;
    justify-content: start;
    margin-top: 16px;
    height: 80px;

    .expenseTitle {
      font-size: 14px;
      font-weight: bold;
      margin-right: auto;
    }

    .InputText {
      width: 200px;
    }

    input[type='file'] {
      display: none;
    }

    .expenseImageUpload {
      cursor: pointer;

      button,
      p,
      img {
        width: 120px;
      }

      img {
        height: 80px;
        object-fit: cover;
        border-radius: 6px;
      }

      p {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        -webkit-text-overflow: ellipsis;
        -o-text-overflow: ellipsis;
      }
    }
  }

  .addExpenseBtn {
    display: flex;
    justify-content: end;
    margin: 20px 0;
    button {
      width: 110px;
    }
  }
`
